import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "~/lib/utils";

const buttonVariants = cva(
  "ekv:inline-flex ekv:items-center ekv:justify-center ekv:gap-2 ekv:whitespace-nowrap ekv:rounded-md ekv:text-sm ekv:font-medium ekv:transition-all ekv:disabled:pointer-events-none ekv:disabled:opacity-50 ekv:[&_svg]:pointer-events-none ekv:[&_svg:not([class*=size-])]:size-4 ekv:shrink-0 ekv:[&_svg]:shrink-0 ekv:outline-none ekv:focus-visible:border-ring ekv:focus-visible:ring-ring/50 ekv:focus-visible:ring-[3px] ekv:aria-invalid:ring-destructive/20 ekv:dark:aria-invalid:ring-destructive/40 ekv:aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          "ekv:bg-primary ekv:text-primary-foreground ekv:hover:bg-primary/90",
        destructive:
          "ekv:bg-destructive ekv:text-white ekv:hover:bg-destructive/90 ekv:focus-visible:ring-destructive/20 ekv:dark:focus-visible:ring-destructive/40 ekv:dark:bg-destructive/60",
        outline:
          "ekv:border ekv:bg-background ekv:shadow-xs ekv:hover:bg-accent ekv:hover:text-accent-foreground ekv:dark:bg-input/30 ekv:dark:border-input ekv:dark:hover:bg-input/50",
        secondary:
          "ekv:bg-secondary ekv:text-secondary-foreground ekv:hover:bg-secondary/80",
        ghost:
          "ekv:hover:bg-accent ekv:hover:text-accent-foreground ekv:dark:hover:bg-accent/50",
        link: "ekv:text-primary ekv:underline-offset-4 ekv:hover:underline",
      },
      size: {
        default: "ekv:h-9 ekv:px-4 ekv:py-2 ekv:has-[>svg]:px-3",
        sm: "ekv:h-8 ekv:rounded-md ekv:gap-1.5 ekv:px-3 ekv:has-[>svg]:px-2.5",
        lg: "ekv:h-10 ekv:rounded-md ekv:px-6 ekv:has-[>svg]:px-4",
        icon: "ekv:size-9",
        "icon-sm": "ekv:size-8",
        "icon-lg": "ekv:size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
